/**
 * React hook for streaming container logs via HTTP chunked transfer.
 * Connects to the /sandboxes/{id}/logstream endpoint for real-time log streaming.
 *
 * @module hooks/useLogStream
 */

import { useRef, useCallback, useState } from 'react';
import { config } from '@/config';

export type LogStreamConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface LogEntry {
  /** Unique identifier for React key prop */
  id: string;
  /** ISO 8601 timestamp */
  timestamp: string;
  /** Stream source: stdout, stderr, or status */
  stream: 'stdout' | 'stderr' | 'status';
  /** Log message content */
  message: string;
}

export interface UseLogStreamOptions {
  sandboxId: string;
  /** Optional sandbox group ID — routes log stream through group-scoped URL */
  sandboxGroupId?: string;
  /** Number of historical lines to fetch (0-300, default: 100) */
  tailLines?: number;
  /** Output format: "text" or "json" (default: "json" for structured parsing) */
  logFormat?: 'text' | 'json';
  /** If true (default), stream continuously; if false, return tail lines and close */
  follow?: boolean;
  /** Maximum number of log entries to keep in memory (default: 1000) */
  maxEntries?: number;
  /** Called when connection state changes */
  onStateChange?: (state: LogStreamConnectionState) => void;
  /** Called when an error occurs */
  onError?: (error: string) => void;
}

export interface UseLogStreamReturn {
  /** Connect to the log stream */
  connect: () => void;
  /** Disconnect from the log stream */
  disconnect: () => void;
  /** Clear all log entries */
  clearLogs: () => void;
  /** Current log entries */
  logs: LogEntry[];
  /** Current connection state */
  connectionState: LogStreamConnectionState;
  /** Current error message, if any */
  error: string | null;
}

/**
 * Hook to stream container logs via HTTP chunked transfer encoding.
 * Uses the Fetch API with ReadableStream to parse NDJSON responses.
 */
export function useLogStream(options: UseLogStreamOptions): UseLogStreamReturn {
  const {
    sandboxId,
    sandboxGroupId,
    tailLines = 100,
    logFormat = 'json',
    follow = true,
    maxEntries = 1000,
    onStateChange,
    onError,
  } = options;

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connectionState, setConnectionState] = useState<LogStreamConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);

  // AbortController for cancelling the fetch request
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track intentional disconnects
  const intentionalDisconnectRef = useRef(false);
  // Track connection state in a ref to avoid stale closure issues
  const connectionStateRef = useRef<LogStreamConnectionState>('disconnected');
  // Counter for generating unique log entry IDs
  const logIdCounterRef = useRef(0);

  const updateConnectionState = useCallback(
    (state: LogStreamConnectionState) => {
      connectionStateRef.current = state;
      setConnectionState(state);
      onStateChange?.(state);
    },
    [onStateChange]
  );

  const updateError = useCallback(
    (err: string | null) => {
      setError(err);
      if (err) {
        onError?.(err);
      }
    },
    [onError]
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    updateConnectionState('disconnected');
  }, [updateConnectionState]);

  const connect = useCallback(() => {
    // Don't reconnect if already connecting or connected (use ref to avoid stale closure)
    if (connectionStateRef.current === 'connecting' || connectionStateRef.current === 'connected') {
      console.log('[LogStream] Already connected or connecting, skipping');
      return;
    }

    // Disconnect existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    intentionalDisconnectRef.current = false;
    updateConnectionState('connecting');
    updateError(null);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const sandboxBase = sandboxGroupId
      ? `${config.apiBaseUrl}/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/sandboxes/${encodeURIComponent(sandboxId)}`
      : `${config.apiBaseUrl}/sandboxes/${encodeURIComponent(sandboxId)}`;
    const url = `${sandboxBase}/logstream?tailLines=${tailLines}&logFormat=${logFormat}&follow=${follow}`;
    console.log('[LogStream] Connecting to:', url);

    fetch(url, {
      method: 'GET',
      credentials: 'include', // Send cookies for auth
      signal: abortController.signal,
      headers: {
        Accept: 'application/x-ndjson',
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          // Try to parse error as ProblemDetails
          let errorMessage = `HTTP ${response.status}`;
          try {
            const errorBody = await response.json();
            errorMessage = errorBody.detail || errorBody.title || errorMessage;
          } catch {
            // Ignore parse errors
          }
          throw new Error(errorMessage);
        }

        if (!response.body) {
          throw new Error('Response body is not readable');
        }

        updateConnectionState('connected');
        console.log('[LogStream] Connected');

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        // Helper to parse a single JSON line
        const parseLogEntry = (line: string): LogEntry | null => {
          const trimmedLine = line.trim();
          if (!trimmedLine) return null;
          try {
            const parsed = JSON.parse(trimmedLine) as Omit<LogEntry, 'id'>;
            // Generate unique ID and normalize stream
            const entry: LogEntry = {
              id: `log-${logIdCounterRef.current++}`,
              timestamp: parsed.timestamp,
              stream: (parsed.stream?.toLowerCase() as LogEntry['stream']) || 'stdout',
              message: parsed.message,
            };
            return entry;
          } catch (parseError) {
            console.warn('[LogStream] Failed to parse line:', trimmedLine, parseError);
            return null;
          }
        };

        // Read chunks from the stream
        const processChunks = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                // Process any remaining data in buffer before closing
                if (buffer.trim()) {
                  const entry = parseLogEntry(buffer);
                  if (entry) {
                    setLogs((prevLogs) => {
                      const updatedLogs = [...prevLogs, entry];
                      if (updatedLogs.length > maxEntries) {
                        return updatedLogs.slice(-maxEntries);
                      }
                      return updatedLogs;
                    });
                  }
                }
                console.log('[LogStream] Stream ended');
                if (!intentionalDisconnectRef.current) {
                  updateConnectionState('disconnected');
                }
                break;
              }

              // Decode chunk and add to buffer
              buffer += decoder.decode(value, { stream: true });

              // Process complete lines (NDJSON format)
              const lines = buffer.split('\n');
              // Keep the last incomplete line in the buffer
              buffer = lines.pop() || '';

              const newEntries: LogEntry[] = [];

              for (const line of lines) {
                const entry = parseLogEntry(line);
                if (entry) {
                  newEntries.push(entry);
                }
              }

              if (newEntries.length > 0) {
                setLogs((prevLogs) => {
                  const updatedLogs = [...prevLogs, ...newEntries];
                  // Trim to max entries
                  if (updatedLogs.length > maxEntries) {
                    return updatedLogs.slice(-maxEntries);
                  }
                  return updatedLogs;
                });
              }
            }
          } finally {
            // Always release the reader lock, even on abort/error
            reader.releaseLock();
          }
        };

        await processChunks();
      })
      .catch((err) => {
        if (intentionalDisconnectRef.current || err.name === 'AbortError') {
          console.log('[LogStream] Connection aborted (intentional)');
          return;
        }

        console.error('[LogStream] Connection error:', err);
        updateError(err.message || 'Connection failed');
        updateConnectionState('error');
      });
  }, [
    sandboxId,
    sandboxGroupId,
    tailLines,
    logFormat,
    follow,
    maxEntries,
    updateConnectionState,
    updateError,
  ]);

  return {
    connect,
    disconnect,
    clearLogs,
    logs,
    connectionState,
    error,
  };
}
